import { MAX_NAME_LENGTH } from '@ofc/shared';
import { useState } from 'react';
import type { useAccount } from '../auth';

/**
 * Optional accounts: signing in, and the name plate and record behind it. Shared
 * by every game's landing screen, so a player's identity is the same throughout
 * Organised Fun Centre.
 */

export function SignIn({ account }: { account: ReturnType<typeof useAccount> }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  if (!open) {
    return (
      <button className="btn-ghost small" onClick={() => setOpen(true)}>
        Sign in to keep stats
      </button>
    );
  }

  const submit = () => {
    void account.authenticate(mode, username.trim(), password);
  };

  return (
    <section className="panel col">
      <div className="row" role="tablist" style={{ gap: 8 }}>
        <button
          role="tab"
          aria-selected={mode === 'login'}
          className="tab"
          onClick={() => setMode('login')}
          style={{ minHeight: 40, fontSize: 'inherit' }}
        >
          Sign in
        </button>
        <button
          role="tab"
          aria-selected={mode === 'signup'}
          className="tab"
          onClick={() => setMode('signup')}
          style={{ minHeight: 40, fontSize: 'inherit' }}
        >
          New account
        </button>
      </div>

      <input
        value={username}
        onChange={(e) => {
          setUsername(e.target.value);
          account.setError(null);
        }}
        placeholder="Username"
        autoCapitalize="none"
        autoCorrect="off"
        autoComplete="username"
        maxLength={MAX_NAME_LENGTH}
      />
      <input
        type="password"
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
          account.setError(null);
        }}
        placeholder="Password"
        autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
        onKeyDown={(e) => e.key === 'Enter' && submit()}
      />

      <button onClick={submit} disabled={account.busy || !username.trim() || !password}>
        {account.busy ? 'Working…' : mode === 'login' ? 'Sign in' : 'Create account'}
      </button>

      {account.error && (
        <p className="small" style={{ color: 'var(--red)', margin: 0 }} role="alert">
          {account.error}
        </p>
      )}

      <p className="small muted" style={{ margin: 0 }}>
        Your password is scrambled on this device before it is sent.
      </p>
    </section>
  );
}

/** The signed-in profile is the wardrobe: what you wear, and what you have earned. */
export { Wardrobe as Profile } from './Wardrobe';
